/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UiSelectWithGraphics, { UiSelectOption } from 'components/Ui/UiSelectWithGraphics'
import UiInput from 'components/Ui/UiInput'
import CompanyFormLabel from 'pages/Front/Booking/components/CompanyForm/CompanyFormLabel'
import clsx from 'clsx'
import { FieldErrors } from 'react-hook-form'
import { useCompanyOriginOptions } from '../hooks/useCompanyOriginOptions'
import {
    isCompanyOriginPreset,
    normalizeCompanyOrigin,
} from 'utils/companies/companyOriginUtils'

interface CompanyOriginSelectProps {
    value: string | null
    onChange: (value: string | null) => void
    error?: string
    className?: string
    required?: boolean
    errors?: FieldErrors
    label?: string
    name?: string
}

const CompanyOriginSelect: React.FC<CompanyOriginSelectProps> = ({
    value,
    onChange,
    error,
    className,
    required,
    errors,
    label,
    name = 'origin',
}) => {
    const { t } = useTranslation()
    const defaultOptions = useCompanyOriginOptions()
    const normalizedValue = normalizeCompanyOrigin(value)
    const [customModeActive, setCustomModeActive] = useState(false)

    const options: UiSelectOption[] = useMemo(
        () => [...defaultOptions, { value: 'custom', label: t('Other') }],
        [defaultOptions, t]
    )

    const selectedOption = useMemo(() => {
        if (normalizedValue && isCompanyOriginPreset(normalizedValue)) {
            return normalizedValue
        }
        if (normalizedValue || customModeActive) {
            return 'custom'
        }
        return null
    }, [normalizedValue, customModeActive])

    const customValue = selectedOption === 'custom' ? normalizedValue : ''

    const handleSelectChange = (val: string | number | null) => {
        const stringVal = val ? String(val) : null
        if (stringVal === 'custom') {
            setCustomModeActive(true)
            onChange(null)
            return
        }

        setCustomModeActive(false)
        onChange(stringVal)
    }

    const handleCustomValueChange = (val: string) => {
        setCustomModeActive(true)
        onChange(val.trim() || null)
    }

    return (
        <div className={clsx('CompanyOriginSelect flex flex-col', className)}>
            <CompanyFormLabel
                label={label ?? t('Erfassungsquelle')}
                name={name}
                required={required}
                errors={errors || {}}
            />
            <UiSelectWithGraphics
                label=""
                options={options}
                selected={selectedOption}
                setSelected={handleSelectChange}
            />
            {selectedOption === 'custom' && (
                <UiInput
                    placeholder={t('Enter custom origin')}
                    value={customValue}
                    onChange={(e) => handleCustomValueChange(e.target.value)}
                    hasError={!!error}
                    autoComplete="one-time-code"
                />
            )}
            {selectedOption === 'custom' && error && (
                <span className="text-xs text-red-500 mt-1">{error}</span>
            )}
        </div>
    )
}

export default CompanyOriginSelect
